import MenuItem from "@/shared/components/MenuItem/MenuItem";
import Menu from "@/shared/components/Menu/Menu";
import Stack from "@/shared/components/Stack/Stack";
import { FC, useState, useRef, ReactNode, useMemo, memo, useEffect } from "react";
import Button from "@/shared/components/Button/Button";
import Text from "@/shared/components/Text/Text";
import { useAppContext } from "@/context/AppContext";
import PreviewScene from "@/shared/components/PreviewScene/PreviewScene";
import Popover from "@/shared/components/Popover/Popover";
import './AddItemPanel.css';
import { Grid } from "@/shared/components/Grid/Grid";

interface ItemButtonProps {
    type: string;
    label: string;
    isUpload?: boolean;
    selectedCategory: string;
    onAdd: (type: string) => void;
}

// Memoized ItemButton component to prevent unnecessary re-renders
const ItemButton: FC<ItemButtonProps> = memo(({ type, label, isUpload, selectedCategory, onAdd }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
    const { createItem } = useAppContext();
    
    // Create a preview item for rendering - only when needed (when hovered)
    const previewItem = useMemo(() => {
        // Only create the item when hovered to save resources
        if (!showPreview) return undefined;
        return createItem(type, 'Preview');
    }, [type, createItem, showPreview]);

    // For effects, use a simplified preview approach
    const isEffect = type.startsWith('effect.');
    
    // Handle hover with delay
    useEffect(() => {
        if (isHovered) {
            // Start timer to show preview after 500ms
            hoverTimerRef.current = setTimeout(() => {
                setShowPreview(true);
            }, 500);
        } else {
            // Clear timer and hide preview when not hovered
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
                hoverTimerRef.current = null;
            }
            setShowPreview(false);
        }
        
        // Cleanup timer on unmount
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
                hoverTimerRef.current = null;
            }
        };
    }, [isHovered]);
    
    // If we can't create a preview item, don't render the button
    if (showPreview && previewItem === null) return null;
    
    // Create the preview content for the popover
    const previewContent = (
        <div className="item-preview-popover">
            <div style={{ width: '200px', height: '200px' }}>
                <PreviewScene 
                    isHovered={true}
                    item={previewItem}
                    type={type}
                />
            </div>
        </div>
    );
    
    // Simple placeholder for the button content
    const buttonContent = (
        <Stack direction="vertical" gap="sm" justify="center" align="center" fill>
            <div className="item-button-placeholder" style={{ 
                width: '48px', 
                height: '48px', 
                backgroundColor: isEffect ? '#4080ff' : '#cccccc',
                opacity: 0.3,
                borderRadius: '4px'
            }} />
            {label}
        </Stack>
    );
    
    return (
        <Popover
            content={previewContent}
            trigger="hover"
            placement="top"
            disabled={!showPreview}
            className="item-preview-popover-container"
            hoverDelay={{ open: 0, close: 300 }}
        >
            <Button
                key={type}
                onClick={() => onAdd(type)}
                style={{ width: '100%' }}
                className="add-item-panel__grid-item"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {buttonContent}
            </Button>
        </Popover>
    );
});

const AddItemPanel: FC = () => {
    const [selectedCategory, setSelectedCategory] = useState<string>('mesh');
    const { activeWorld, addMesh, addLight, addEffect, addLink, addMaterial, definitions } = useAppContext();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Group items by category
    const categories = Object.entries(definitions).reduce((acc, [type, def]) => {
        const [category] = type.split('.');
        if (!acc[category]) {
            acc[category] = [];
        }
        
        // Get the display name from the subtype or from the definition's name
        const subtype = type.split('.')[1] || '';
        const displayName = def.name || subtype.charAt(0).toUpperCase() + subtype.slice(1);
        
        acc[category].push({
            type,
            label: displayName,
            isUpload: type === 'mesh.fbx'
        });
        return acc;
    }, {} as Record<string, Array<{ type: string; label: string; isUpload?: boolean }>>);

    const handleAddItem = (type: string) => {
        if (!activeWorld) return;

        const [baseType, subtype] = type.split('.');
        switch (baseType) {
            case 'mesh':
                if (subtype === 'fbx') {
                    fileInputRef.current?.click();
                } else {
                    addMesh(subtype);
                }
                break;
            case 'light':
                addLight(subtype);
                break;
            case 'effect':
                addEffect(subtype);
                break;
            case 'link':
                addLink(subtype);
                break;
            case 'material':
                addMaterial(subtype);
                break;
            default:
                break;
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !activeWorld) return;

        // Create a URL for the uploaded file
        const fbxUrl = URL.createObjectURL(file);

        // Add the mesh with the FBX URL
        addMesh('fbx', { fbxUrl });

        // Reset the file input
        event.target.value = '';
    };

    return (
        <Stack className="add-item-panel" direction="horizontal" gap="no">
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".fbx"
                onChange={handleFileUpload}
            />
            <Stack className="add-item-panel__sidebar">
                <Menu>
                    {Object.keys(categories).map(category => (
                        <MenuItem
                            key={category}
                            isSelected={selectedCategory === category}
                            onClick={() => setSelectedCategory(category)}
                        >
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                        </MenuItem>
                    ))}
                </Menu>
            </Stack>
            <Stack className="add-item-panel__content" direction="vertical" gap="sm" fill>
                <Text variant="label" as="h3">{selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} items</Text>
                <Grid columns={7} gap="sm" className="add-item-panel__grid">
                    {categories[selectedCategory]?.map((item) => (
                        <ItemButton
                            key={item.type}
                            type={item.type}
                            label={item.label}
                            isUpload={item.isUpload}
                            selectedCategory={selectedCategory}
                            onAdd={handleAddItem}
                        />
                    ))}
                </Grid>
            </Stack>
        </Stack>
    );
};

export default AddItemPanel;